import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import SignInPage from './pages/Auth/SignIn/SignInPage';
import SignUpPage from './pages/Auth/SignUp/SignUpPage';
import AppTheme from './components/shared-theme/AppTheme';
import { CssBaseline } from '@mui/material';
import Hero from './components/marketing-page/components/Hero';
import { createStore, applyMiddleware } from 'redux';
import appReducer from './configuration/redux/reducers';
import { thunk } from 'redux-thunk';
import { Provider } from 'react-redux';
import UserProfile from './pages/UserProfile/UserProfile';
import SelectUserCategories from './pages/Categories/SelectUserCategories/SelectUserCategories';
import OnboardingProvider from './components/HoC/OnboardingProvider';
import AuthProvider from './components/HoC/AuthProvider';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import LogVisitsProvider from './components/HoC/LogVisitsProvider';
import BrowseHandymanPage from './pages/BrowseHandyman/BrowseHandymanPage';
import HandymanDetails from './pages/BrowseHandyman/HandymanDetails/HandymanDetails';
import OrdersPage from './pages/Orders/OrdersPage';
import CreateOrderPage from './pages/Orders/CreateOrderPage';
import OrderDetailsPage from './pages/Orders/OrderDetailsPage';
import ChatsPage from './pages/Chats/ChatsPage';
import OrderReviewsPage from './pages/Reviews/OrderReviewsPage';
import UserReviewsPage from './pages/Reviews/UserReviewsPage';
import OrderProposalsPage from './pages/Proposals/OrderProposalsPage';
import GoogleMapsWrapper from './pages/UserProfile/components/UserDetails/GoogleMapsWrapper';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import SupportPage from './pages/Support/SupportPage';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import AboutUsPage from './pages/AboutUs/AboutUsPage';
import TermsOfServicePage from './pages/TermsOfService/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicy/PrivacyPolicyPage';
import ContactUsPage from './pages/ContactUs/ContactUsPage';
import CookiesPage from './pages/Cookies/CookiesPage';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import CookieBanner from './components/CookieBanner/CookieBanner';
import { MainLayout } from './layouts/MainLayout';
import { useZendeskHideOnCertainPages } from './hooks/useZendeskHideOnCertainPages';
import { usePageTracking } from './hooks/usePageTracking';
import { useClickTracking } from './hooks/useClickTracking';
import { HelpBlogPage } from './pages/HelpBlog/HelpBlogPage';
import { HelpBlogArticle } from './pages/HelpBlog/articles/HelpBlogArticle';
import { NotFound } from './pages/NotFound/NotFound';
import { Error500 } from './pages/Error500/Error500';
import VerificationPage from './pages/Verification/VerificationPage';
import ForProPage from './pages/ForProPage/ForProPage';
import FeesPage from './pages/FeesPage/FeesPage';
import RefundPage from './pages/RefundPage/RefundPage';
import ProposalsPage from './pages/ProposalsPage/ProposalsPage';
import EarlyBirdPage from './pages/EarlyBirdPage/EarlyBirdPage';

const middlewares = [thunk as any];
export const store = createStore(appReducer, applyMiddleware(...middlewares));
export type AppDispatch = typeof store.dispatch;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

const App = () => {
  const ZendeskVisibility = () => {
    useZendeskHideOnCertainPages();
    return null;
  };
  const PageTracking = () => {
    usePageTracking();
    return null;
  };
  const ClickTracking = () => {
    useClickTracking();
    return null;
  };
  return (
    <Provider store={store}>
      <AuthProvider>
        <GoogleMapsWrapper>
          <AppTheme>
            <BrowserRouter>
              <ZendeskVisibility />
              <PageTracking />
              <ClickTracking />
              <ScrollToTop />
              <LogVisitsProvider />
              <OnboardingProvider>
                <CssBaseline enableColorScheme />

                <Routes>
                  {/* Pages without header and footer */}
                  <Route path='sign-up' element={<SignUpPage />} />
                  <Route
                    path='select-categories'
                    element={
                      <ProtectedRoute>
                        <SelectUserCategories />
                      </ProtectedRoute>
                    }
                  />
                  <Route path='verification' element={<VerificationPage />} />
                  <Route
                    path='create-order'
                    element={
                      <ProtectedRoute>
                        <CreateOrderPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Public Routes */}
                  <Route element={<MainLayout />}>
                    <Route path='/' element={<Hero />} />
                    <Route path='sign-in' element={<SignInPage />} />
                    {/* <Route path='sign-up' element={<SignUpPage />} /> */}
                    <Route path='support' element={<SupportPage />} />
                    <Route path='help-blog' element={<HelpBlogPage />} />
                    <Route
                      path='help-blog/:slug'
                      element={<HelpBlogArticle />}
                    />
                    <Route path='about-us' element={<AboutUsPage />} />
                    <Route path='platform-fees' element={<FeesPage />} />
                    <Route path='refund-policy' element={<RefundPage />} />
                    <Route path='for-pro' element={<ForProPage />} />
                    <Route path='early-bird' element={<EarlyBirdPage />} />
                    <Route
                      path='terms-of-service'
                      element={<TermsOfServicePage />}
                    />
                    <Route
                      path='privacy-policy'
                      element={<PrivacyPolicyPage />}
                    />
                    <Route path='contact-us' element={<ContactUsPage />} />
                    <Route path='cookies' element={<CookiesPage />} />
                    <Route
                      path='notifications'
                      element={
                        <ProtectedRoute>
                          <NotificationsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='*' element={<NotFound />} />
                    <Route path='/500' element={<Error500 />} />

                    {/* Protected Routes */}
                    <Route
                      path='profile'
                      element={
                        <ProtectedRoute>
                          <UserProfile />
                        </ProtectedRoute>
                      }
                    />
                    {/* <Route
                      path='select-categories'
                      element={
                        <ProtectedRoute>
                          <SelectUserCategories />
                        </ProtectedRoute>
                      }
                    /> */}
                    <Route
                      path='browse-handyman'
                      element={<BrowseHandymanPage />}
                    />
                    <Route
                      path='browse-handyman/:userId'
                      element={<HandymanDetails />}
                    />
                    <Route
                      path='orders'
                      element={
                        <ProtectedRoute>
                          <OrdersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/proposals' element={<ProposalsPage />} />
                    <Route
                      path='orders/:orderId'
                      element={
                        <ProtectedRoute>
                          <Elements stripe={stripePromise}>
                            <OrderDetailsPage />
                          </Elements>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='chats'
                      element={
                        <ProtectedRoute>
                          <ChatsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='chats/:chatId'
                      element={
                        <ProtectedRoute>
                          <ChatsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='reviews/order/:orderId'
                      element={
                        <ProtectedRoute>
                          <OrderReviewsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='reviews/user/:userId'
                      element={
                        <ProtectedRoute>
                          <UserReviewsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='proposals/order/:orderId'
                      element={
                        <ProtectedRoute children={<OrderProposalsPage />} />
                      }
                    />
                  </Route>
                </Routes>
                <CookieBanner />
              </OnboardingProvider>
            </BrowserRouter>
          </AppTheme>
        </GoogleMapsWrapper>
      </AuthProvider>
    </Provider>
  );
};

export default App;
